import React, { Component } from "react";
import { observer } from "mobx-react";
import { AxiosResponse } from "axios";
import { getRequest } from "../../../utils/httpRequest";
import { CertificatesResource } from "../../../stores/CoursesStore/Types";
import CertificateImage from "./CertificateImage";

interface State {
  certificates: CertificatesResource;
  loaded: boolean;
  canScrollCertificates: boolean;
  canScrollBack: boolean;
  canScrollForward: boolean;
}

class Certificates extends Component<{}, State> {
  certificateRailRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      certificates: [],
      loaded: false,
      canScrollCertificates: false,
      canScrollBack: false,
      canScrollForward: false,
    };
  }

  componentDidMount() {
    window.addEventListener("resize", this.updateCarouselControls);

    return getRequest("/api/profile/certificates.json").then(
      (response: AxiosResponse<CertificatesResource>) => {
        this.setState({ certificates: response.data, loaded: true }, () => {
          this.updateCarouselControls();
          this.scrollToSectionIfHashPresent();
        });
      },
    );
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateCarouselControls);
  }

  // When arriving from the "My certificates" menu link (/profile#certificates),
  // scroll to this section once its data has loaded and it has rendered.
  scrollToSectionIfHashPresent = () => {
    if (window.location.hash !== "#certificates") {
      return;
    }

    const section = document.getElementById("certificates");
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  updateCarouselControls = () => {
    const rail = this.certificateRailRef.current;

    if (!rail) {
      return;
    }

    const canScrollCertificates = rail.scrollWidth > rail.clientWidth + 2;
    const canScrollBack = canScrollCertificates && rail.scrollLeft > 2;
    const canScrollForward =
      canScrollCertificates &&
      rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 2;

    if (
      canScrollCertificates !== this.state.canScrollCertificates ||
      canScrollBack !== this.state.canScrollBack ||
      canScrollForward !== this.state.canScrollForward
    ) {
      this.setState({
        canScrollCertificates,
        canScrollBack,
        canScrollForward,
      });
    }
  };

  scrollCertificates = (direction: "back" | "forward") => {
    const rail = this.certificateRailRef.current;

    if (!rail) {
      return;
    }

    const firstTile = rail.querySelector(".cert-tile") as HTMLElement;
    const tileStep = firstTile ? firstTile.offsetWidth + 20 : 300;
    const left = tileStep * 3 * (direction === "back" ? -1 : 1);

    rail.scrollBy({ left, behavior: "smooth" });
  };

  render() {
    const {
      loaded,
      certificates,
      canScrollCertificates,
      canScrollBack,
      canScrollForward,
    } = this.state;
    const earnedCertificates = certificates?.filter(
      (certificate) => certificate.passed,
    ).length;

    return (
      <section
        className="section container profile-certificates"
        aria-labelledby="certificates-heading"
        id="certificates"
      >
        <div className="section-top">
          <div>
            <h2 className="section-heading" id="certificates-heading">
              Certificates
            </h2>
            <p className="section-sub">Your trophy wall in miniature.</p>
          </div>

          <div className="cert-count">
            <strong>{earnedCertificates}</strong> of{" "}
            <span>{certificates.length}</span> earned
          </div>
        </div>

        {!loaded && (
          <div
            className="profile-section-loader profile-section-loader--certificates"
            aria-live="polite"
          >
            <div className="profile-section-loader__cert" />
            <div className="profile-section-loader__cert" />
            <div className="profile-section-loader__cert" />
            <div className="profile-section-loader__cert" />
          </div>
        )}

        <div className="carousel is-cert" id="certificates_grid">
          <button
            className={`carousel-nav prev ${
              canScrollCertificates ? "show" : ""
            }`}
            type="button"
            aria-label="Scroll certificates back"
            disabled={!canScrollBack}
            onClick={() => this.scrollCertificates("back")}
          >
            <svg aria-hidden="true" viewBox="0 0 16 16">
              <path d="M10 3 L5 8 L10 13" />
            </svg>
          </button>

          <button
            className={`carousel-nav next ${
              canScrollCertificates ? "show" : ""
            }`}
            type="button"
            aria-label="Scroll certificates forward"
            disabled={!canScrollForward}
            onClick={() => this.scrollCertificates("forward")}
          >
            <svg aria-hidden="true" viewBox="0 0 16 16">
              <path d="M6 3 L11 8 L6 13" />
            </svg>
          </button>

          <div
            className="carousel-rail"
            id="certRail"
            ref={this.certificateRailRef}
            tabIndex={0}
            onScroll={this.updateCarouselControls}
          >
            {certificates.map((course, i) => (
              <CertificateImage
                course={course}
                idx={i}
                key={course.slug || i}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }
}
export default observer(Certificates);
